import {
    AgDocument,
    BaseProperties,
    CleanupRegistry,
    type Placement,
    Property,
    calculatePlacement,
    clamp,
    isNode,
} from 'ag-charts-core';
import type { AgTooltipAnchorTo, AgTooltipMode, AgTooltipPlacement, InteractionRange, TextWrap } from 'ag-charts-types';

import type { DOMElementProxy } from '../../dom/domElementProxy';
import type { DOMManager } from '../../dom/domManager';
import type { LocaleManager } from '../../locale/localeManager';
import { SpringAnimation } from './springAnimation';
import { getTooltipBounds } from './tooltipBounds';
import {
    DEFAULT_TOOLTIP_CLASS,
    DEFAULT_TOOLTIP_DARK_CLASS,
    type TooltipContent,
    type TooltipPaginationState,
    tooltipHtml,
} from './tooltipContent';

export {
    DEFAULT_TOOLTIP_CLASS,
    DEFAULT_TOOLTIP_DARK_CLASS,
    tooltipHtml,
    tooltipContentAriaLabel,
    isTooltipValueMissing,
    type TooltipContent,
    type TooltipPaginationState,
    type TooltipContentDataRow,
    type TooltipStructuredContent,
} from './tooltipContent';

/** Default gap between tooltip and anchor; must match the theme template fallback in chartTheme.ts. */
const DEFAULT_TOOLTIP_OFFSET = 12;

type TooltipOffsets = { canvasX: number; canvasY: number; nodeCanvasX?: number; nodeCanvasY?: number };
export type TooltipEventType = 'pointermove' | 'click' | 'dblclick' | 'keyboard';
export type TooltipPointerEvent<T extends TooltipEventType = TooltipEventType> = Readonly<TooltipOffsets> & {
    readonly type: T;
};

export interface TooltipMetaPosition {
    anchorTo?: AgTooltipAnchorTo;
    placement?: AgTooltipPlacement | AgTooltipPlacement[];
    xOffset?: number;
    yOffset?: number;
    offset?: number;
}

export interface TooltipMeta extends TooltipOffsets {
    showArrow?: boolean;
    position?: TooltipMetaPosition;
    enableInteraction?: boolean;
}

enum ArrowPosition {
    Left,
    Top,
    Bottom,
    Right,
}

const arrowPositions: Record<AgTooltipPlacement, ArrowPosition | undefined> = {
    left: ArrowPosition.Right,
    'top-left': undefined,
    'bottom-left': undefined,
    top: ArrowPosition.Bottom,
    center: undefined,
    bottom: ArrowPosition.Top,
    right: ArrowPosition.Left,
    'top-right': undefined,
    'bottom-right': undefined,
};

enum DirectionCheck {
    Horizontal = 0b01,
    Vertical = 0b10,
    Both = 0b11,
    None = 0b00,
}

const directionChecks: Record<AgTooltipPlacement, DirectionCheck> = {
    top: DirectionCheck.Vertical,
    bottom: DirectionCheck.Vertical,
    left: DirectionCheck.Horizontal,
    right: DirectionCheck.Horizontal,
    'top-right': DirectionCheck.Both,
    'top-left': DirectionCheck.Both,
    'bottom-right': DirectionCheck.Both,
    'bottom-left': DirectionCheck.Both,
    center: DirectionCheck.None,
};

const defaultPlacements: Record<AgTooltipAnchorTo, AgTooltipPlacement | AgTooltipPlacement[]> = {
    pointer: ['top', 'bottom', 'left', 'right'],
    node: ['top', 'bottom', 'left', 'right'],
    chart: 'top-left',
};

export class TooltipPosition extends BaseProperties {
    @Property
    /** The horizontal offset in pixels for the position of the tooltip. */
    xOffset: number = 0;

    @Property
    /** The vertical offset in pixels for the position of the tooltip. */
    yOffset: number = 0;

    /** The distance in pixels between the tooltip and its anchor point, applied in the placement direction. */
    @Property
    offset?: number;

    @Property
    anchorTo?: AgTooltipAnchorTo;

    @Property
    placement?: AgTooltipPlacement | AgTooltipPlacement[];
}

export class Tooltip extends BaseProperties {
    @Property
    enabled: boolean = true;

    @Property
    mode: AgTooltipMode = 'single';

    @Property
    showArrow?: boolean;

    @Property
    delay: number = 0;

    @Property
    range?: InteractionRange = undefined;

    @Property
    wrapping: TextWrap = 'hyphenate';

    @Property
    readonly position = new TooltipPosition();

    @Property
    readonly pagination = false;

    @Property
    darkTheme = false;

    /** Escape-hatch for changes in AG-11645. */
    @Property
    bounds: 'extended' | 'canvas' = 'extended';

    private readonly cleanup = new CleanupRegistry();
    private readonly springAnimation = new SpringAnimation(this.agDocument);

    private enableInteraction: boolean = false;
    private readonly wrapTypes = ['always', 'hyphenate', 'on-space', 'never'];

    private interactiveLeave?: {
        callback: () => void;
        listener: (e: MouseEvent | FocusEvent) => void;
    };

    // Reading the element size is expensive, so cache the result
    private _elementSize: { width: number; height: number } | undefined = undefined;
    private _showTimeout: NodeJS.Timeout | undefined = undefined;
    private arrowPosition: ArrowPosition | undefined = undefined;
    private _visible = false;

    private elementProxy?: DOMElementProxy;
    private domManager?: DOMManager;

    private positionParams:
        | {
              canvasRect: DOMRect;
              relativeRect: { x: number; y: number; width: number; height: number };
              meta: TooltipMeta;
          }
        | undefined = undefined;

    get interactive() {
        return this.enableInteraction;
    }

    constructor(private readonly agDocument: AgDocument) {
        super();

        this.cleanup.register(
            this.springAnimation.events.on('update', () => {
                this.updateTooltipPosition();
            })
        );
    }

    private localeManager: LocaleManager | undefined = undefined;
    setup(localeManager: LocaleManager, domManager: DOMManager) {
        this.domManager = domManager;
        this.elementProxy = domManager.addDeferredProxyChild('tooltip-container', DEFAULT_TOOLTIP_CLASS);
        this.elementProxy.toggleClass(DEFAULT_TOOLTIP_CLASS, true);
        this.elementProxy.setProperty('position-anchor', domManager.anchorName);
        this.elementProxy.setAttr('popover', 'manual');

        const removeResizeListener = this.elementProxy.addResizeListener((size) => {
            this._elementSize = size;
            this.updateTooltipPosition();
        });
        this.localeManager = localeManager;

        return () => {
            domManager.removeChild('tooltip-container', DEFAULT_TOOLTIP_CLASS);
            this.cleanup.flush();
            removeResizeListener();
        };
    }

    isVisible(): boolean {
        return this._visible;
    }

    contains(node: Node | null): boolean {
        return this.elementProxy?.contains(node) ?? false;
    }

    private updateTooltipPosition() {
        const { elementProxy, _elementSize: elementSize, positionParams } = this;
        if (elementProxy == null || elementSize == null || positionParams == null) return;

        const { canvasRect, relativeRect, meta } = positionParams;
        const { x: canvasX, y: canvasY } = this.springAnimation;

        const anchorTo = meta.position?.anchorTo ?? 'pointer';
        let placements = meta.position?.placement ?? defaultPlacements[anchorTo];
        if (!Array.isArray(placements)) {
            placements = [placements];
        }
        const xOffset = meta.position?.xOffset ?? 0;
        const yOffset = meta.position?.yOffset ?? 0;
        const offset = meta.position?.offset ?? DEFAULT_TOOLTIP_OFFSET;

        const minX = relativeRect.x;
        const minY = relativeRect.y;
        const maxX = relativeRect.width - elementSize.width - 1 + minX;
        const maxY = relativeRect.height - elementSize.height + minY;

        let i = 0;
        let placement: AgTooltipPlacement | undefined;
        let position: Placement | undefined;
        let constrained = false;
        do {
            placement = placements[i];
            i += 1;

            const tooltipBounds = getTooltipBounds({
                elementSize,
                placement,
                anchorTo,
                canvasX,
                canvasY,
                yOffset,
                xOffset,
                offset,
                canvasRect,
            });
            position = calculatePlacement(elementSize.width, elementSize.height, relativeRect, tooltipBounds);

            constrained = false;
            if (directionChecks[placement] & DirectionCheck.Horizontal) {
                constrained ||= position.x < minX || position.x > maxX;
            }
            if (directionChecks[placement] & DirectionCheck.Vertical) {
                constrained ||= position.y < minY || position.y > maxY;
            }
        } while (i < placements.length && constrained);

        const left = clamp(minX, position.x, maxX);
        const top = clamp(minY, position.y, maxY);

        constrained ||= left !== position.x || top !== position.y;
        const defaultShowArrow = anchorTo !== 'chart' && !constrained && !xOffset && !yOffset;
        const showArrow = meta.showArrow ?? this.showArrow ?? defaultShowArrow;
        this.arrowPosition = showArrow ? arrowPositions[placement] : undefined;
        this.updateClassModifiers();

        elementProxy.setProperty('translate', `${left}px ${top}px`);
    }

    /**
     * Confine an anchor point (canvas-local, screen px) to the visible chart area so a node- or
     * pointer-anchored tooltip stays connected to the chart when its anchor has scrolled out of a
     * scrollable container (AG-13992). The tooltip body itself remains free to float to the
     * viewport. No-op when the chart has no scrollable ancestor.
     */
    private clampAnchorToVisibleChart(x: number, y: number, canvasRect: DOMRect): { x: number; y: number } {
        const visibleRect = this.domManager?.getVisibleChartRect();
        if (visibleRect == null) return { x, y };
        return {
            x: clamp(visibleRect.left - canvasRect.left, x, visibleRect.right - canvasRect.left),
            y: clamp(visibleRect.top - canvasRect.top, y, visibleRect.bottom - canvasRect.top),
        };
    }

    /**
     * Shows tooltip at the given event's coordinates.
     * If the `html` parameter is missing, moves the existing tooltip to the new position.
     */
    show(
        boundingRect: DOMRect,
        canvasRect: DOMRect,
        meta: TooltipMeta,
        content: TooltipContent[] | null,
        pagination?: TooltipPaginationState,
        instantly = false
    ) {
        const { elementProxy } = this;

        if (elementProxy != null && content != null && content.length !== 0) {
            const html = tooltipHtml(this.localeManager, content, this.mode, this.pagination ? pagination : undefined);
            if (html == null) {
                elementProxy.setInnerHTML('');
                this.toggle(false);
                return;
            }

            elementProxy.setInnerHTML(html);
        } else if (elementProxy == null || elementProxy.innerHTML === '') {
            this.toggle(false);
            return;
        }

        const relativeRect = {
            x: boundingRect.x - canvasRect.x,
            y: boundingRect.y - canvasRect.y,
            width: boundingRect.width,
            height: boundingRect.height,
        };

        this.positionParams = {
            canvasRect,
            relativeRect,
            meta,
        };

        // Tooltip is `position: fixed` (screen space); coords are canvas-local — scale to match.
        const { scaleX, scaleY } = this.domManager?.getCanvasScale() ?? { scaleX: 1, scaleY: 1 };
        const anchorTo = meta.position?.anchorTo ?? 'pointer';
        switch (anchorTo) {
            case 'node': {
                const { x, y } = this.clampAnchorToVisibleChart(
                    (meta.nodeCanvasX ?? meta.canvasX) * scaleX,
                    (meta.nodeCanvasY ?? meta.canvasY) * scaleY,
                    canvasRect
                );
                this.springAnimation.update(x, y);
                break;
            }
            case 'pointer': {
                const { x, y } = this.clampAnchorToVisibleChart(
                    meta.canvasX * scaleX,
                    meta.canvasY * scaleY,
                    canvasRect
                );
                this.springAnimation.update(x, y);
                break;
            }
            case 'chart':
                this.springAnimation.reset();
        }

        this.enableInteraction = meta.enableInteraction ?? false;

        elementProxy.setProperty('pointer-events', this.enableInteraction ? 'auto' : 'none');
        elementProxy.setAttr('aria-hidden', this.enableInteraction ? null : 'true');
        elementProxy.setAttr('tabindex', this.enableInteraction ? '-1' : null); // AG-14347

        elementProxy.setProperty('--top', `${canvasRect.top}px`);
        elementProxy.setProperty('--left', `${canvasRect.left}px`);
        this.updateClassModifiers();

        this.toggle(true, instantly);
    }

    hide() {
        this.toggle(false);
    }

    maybeEnterInteractiveTooltip({ relatedTarget }: FocusEvent | MouseEvent, callback: () => void): boolean {
        const { interactive, interactiveLeave, enabled, elementProxy } = this;
        if (elementProxy == null) return false;
        if (interactiveLeave) return true;

        const isEntering =
            interactive && enabled && this.isVisible() && isNode(relatedTarget) && this.contains(relatedTarget);

        // AG-14990 Add a custom listener to dismiss the tooltip when the user is done interacting with it.
        if (isEntering) {
            this.interactiveLeave = {
                callback,
                listener: (popoverEvent: FocusEvent | MouseEvent): void => {
                    const isLeaving =
                        popoverEvent.relatedTarget == null ||
                        (isNode(popoverEvent.relatedTarget) && !this.contains(popoverEvent.relatedTarget));
                    if (isLeaving) {
                        this.popInteractiveLeaveCallback();
                    }
                },
            };
            elementProxy.addEventListener('focusout', this.interactiveLeave.listener);
            elementProxy.addEventListener('mouseout', this.interactiveLeave.listener);
        }

        return isEntering;
    }

    private popInteractiveLeaveCallback() {
        const { interactiveLeave, elementProxy } = this;
        this.interactiveLeave = undefined; // prevent re-entrancy.
        if (interactiveLeave) {
            if (elementProxy) {
                elementProxy.removeEventListener('focusout', interactiveLeave.listener);
                elementProxy.removeEventListener('mouseout', interactiveLeave.listener);
            }
            interactiveLeave.callback();
        }
    }

    private toggle(visible: boolean, instantly: boolean = false) {
        const { delay } = this;

        if (visible && delay > 0 && !instantly) {
            this._showTimeout ??= setTimeout(() => {
                this._showTimeout = undefined;
                this.toggleCallback(true);
            }, delay);
        } else {
            clearTimeout(this._showTimeout);
            this._showTimeout = undefined;
            this.toggleCallback(visible);
        }
    }

    private toggleCallback(visible: boolean) {
        if (!this.elementProxy?.isConnected) return;

        // Avoid touching the DOM if invisible and visibility status hasn't changed.
        if (this._visible === visible) return;
        this._visible = visible;

        this.elementProxy.togglePopover(visible);

        if (visible) {
            this.updateTooltipPosition();
        } else {
            this.springAnimation.reset();
            this.popInteractiveLeaveCallback();
            this.elementProxy.reset();
        }
    }

    private updateClassModifiers() {
        if (!this.elementProxy?.isConnected) return;

        const { elementProxy } = this;
        const { enableInteraction, arrowPosition, mode, darkTheme, wrapping } = this;

        const toggle = (name: string, force: boolean) =>
            elementProxy.toggleClass(`${DEFAULT_TOOLTIP_CLASS}--${name}`, force);

        toggle('no-interaction', !enableInteraction);
        toggle('arrow-top', arrowPosition === ArrowPosition.Top);
        toggle('arrow-right', arrowPosition === ArrowPosition.Right);
        toggle('arrow-bottom', arrowPosition === ArrowPosition.Bottom);
        toggle('arrow-left', arrowPosition === ArrowPosition.Left);
        toggle('compact', mode === 'compact');

        elementProxy.toggleClass(DEFAULT_TOOLTIP_DARK_CLASS, darkTheme);

        for (const wrapType of this.wrapTypes) {
            elementProxy.toggleClass(`${DEFAULT_TOOLTIP_CLASS}--wrap-${wrapType}`, wrapType === wrapping);
        }
    }
}
